/**
 * Abode Inline Search — custom element for the inline expandable header search bar.
 * Manages expand/collapse animation, focus, click-outside, and Escape key.
 * Works with the existing <predictive-search> or <search-form> web components.
 */
class AbodeInlineSearch extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.abode-inline-search__trigger');
    this.bar = this.querySelector('.abode-inline-search__bar');
    this.input = this.querySelector('.abode-inline-search__input');
    this.isExpanded = false;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
  }

  connectedCallback() {
    this.trigger.addEventListener('click', this.onTriggerClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onClickOutside);
  }

  onTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isExpanded = true;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.bar.setAttribute('aria-hidden', 'false');

    // Focus the input after the CSS transition starts
    requestAnimationFrame(() => {
      this.input.focus();
    });

    // Listen for clicks outside to close
    setTimeout(() => {
      document.addEventListener('click', this.onClickOutside);
    }, 0);
  }

  close() {
    this.isExpanded = false;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.bar.setAttribute('aria-hidden', 'true');

    // Close predictive search results if open
    const predictiveSearch = this.querySelector('predictive-search');
    if (predictiveSearch && predictiveSearch.close) {
      predictiveSearch.close(true);
    }

    document.removeEventListener('click', this.onClickOutside);
    this.trigger.focus();
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.isExpanded) {
      e.preventDefault();
      this.close();
    }
  }

  onClickOutside(e) {
    if (!this.contains(e.target)) {
      this.close();
    }
  }
}

customElements.define('abode-inline-search', AbodeInlineSearch);
